import { useState, useMemo, useCallback } from "react"
import { Button } from "@shared/components/ui/button"
import { Input } from "@shared/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select"
import { FilterButton } from "@shared/components/filters/base/FilterButton"
import { useAmountFilter } from "../../hooks/useAmountFilter"
import type { AmountFilterValue } from "@shared/types/amountFilter"
import type { FilterConfig } from "@shared/types/filters"
import { CornerDownRight } from "lucide-react"

export interface AmountFilterDropdownProps {
  value?: AmountFilterValue | null
  onChange?: (value: AmountFilterValue | null) => void
  className?: string
  filterConfig: FilterConfig
  currency?: string
}

const OPERATOR_OPTIONS = [
  { value: "equal" as const, label: "is equal to" },
  { value: "range" as const, label: "is between" },
  { value: "greater_than" as const, label: "is greater than" },
  { value: "less_than" as const, label: "is less than" },
] as const

const OPERATOR_DISPLAY_MAP = {
  equal: (amount: string) => `Exactly ${amount}`,
  less_than: (amount: string) => `Less than ${amount}`,
  greater_than: (amount: string) => `Greater than ${amount}`,
} as const

export function AmountFilterDropdown({
  value,
  onChange,
  filterConfig,
}: AmountFilterDropdownProps) {
  const {
    tempValue,
    handleOperatorChange,
    handleInputChange,
    handleApply,
    handleClear,
    isValid,
    setTempValue,
  } = useAmountFilter(value, onChange)

  const [isOpen, setIsOpenRaw] = useState(false)

  const setIsOpen = useCallback((open: boolean) => {
    if (open) {
      setTempValue(value || { operator: "equal", min: "", max: "", value: "" })
    }
    setIsOpenRaw(open)
  }, [value, setTempValue])

  const handleApplyAndClose = () => {
    handleApply()
    setIsOpen(false)
  }

  const handleClearAndReset = () => {
    handleClear()
    setTempValue({ operator: "equal", min: "", max: "", value: "" })
  }

  const displayValue = useMemo(() => {
    if (!value) return ""

    if (value.operator === "range") {
      const min = value.min ? String(value.min) : ""
      const max = value.max ? String(value.max) : ""
      if (min && max) return `${min} to ${max}`
      if (min) return `From ${min}`
      if (max) return `Up to ${max}`
      return ""
    }

    if (value.value && value.operator in OPERATOR_DISPLAY_MAP) {
      return OPERATOR_DISPLAY_MAP[
        value.operator as keyof typeof OPERATOR_DISPLAY_MAP
      ](String(value.value))
    }

    return value.value ? String(value.value) : ""
  }, [value])

  const filterButtonProps = {
    filter: filterConfig,
    isActive: !!value,
    isOpen,
    displayValue: {
      label: filterConfig.label,
      displayValue,
    },
    onToggle: setIsOpen,
    onClear: handleClearAndReset,
  }

  return (
    <FilterButton {...filterButtonProps}>
      <div className="p-3 space-y-3 select-none w-[240px]">
        <div className="font-medium text-sm">Filter by Amount</div>

        <div className="space-y-2">
          <Select
            value={tempValue.operator}
            onValueChange={handleOperatorChange}
          >
            <SelectTrigger className="h-8 rounded-lg border border-input font-normal">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-md shadow-lg border select-none">
              {OPERATOR_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="font-normal text-sm rounded-md"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {tempValue.operator === "range" ? (
            <div className="flex items-center gap-2 pt-1">
              <CornerDownRight className="h-4 w-4 text-primary shrink-0" strokeWidth={3} />
              <Input
                type="number"
                placeholder="0"
                value={tempValue.min || ""}
                onChange={(e) => handleInputChange("min", e.target.value)}
                className="h-8 rounded-lg border border-input w-20 select-text"
              />
              <span className="text-sm text-muted-foreground">and</span>
              <Input
                type="number"
                placeholder="0"
                value={tempValue.max || ""}
                onChange={(e) => handleInputChange("max", e.target.value)}
                className="h-8 rounded-lg border border-input w-20 select-text"
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 pt-1">
              <CornerDownRight className="h-4 w-4 text-primary shrink-0" strokeWidth={3} />
              <Input
                type="number"
                placeholder="0"
                value={tempValue.value || ""}
                onChange={(e) => handleInputChange("value", e.target.value)}
                className="h-8 rounded-lg border border-input w-full select-text"
              />
            </div>
          )}
        </div>

        <div className="pt-1">
          <Button
            onClick={handleApplyAndClose}
            disabled={!isValid}
            className="w-full h-8 rounded-full"
            size="sm"
          >
            Apply
          </Button>
        </div>
      </div>
    </FilterButton>
  )
} 
